'use strict';

/**
 * @module logger/Logger
 * @memberof kernel
 * @description Replaces console logging and provides access to logging which has levels which can be enabled or disabled via RGS.
 * @example
 * var Logger = require('kernel/Logger/logger;
 * Logger.warn('Origin file', 'Warning, failed message');
 *
 * Message displayed will be in the format of
 * ERRORLEVEL [DateTimeISO] origin other arguments, eg:
 * WARN [2024-07-15T13:57:39.005Z] Origin File Warning, failed message
 */
//define(function Logger(){

	/**
	 * @type {object}
	 * @description Log level mapping 
	 * @private
	 */
	 var LEVELS = { //These are the RGS Log Levels.  They map to debug=log, info=info, warn=warn, error=error, fatal=error probably
	 	'ALL': 10,
		'DEBUG': 5, //log
		'INFO': 4, //info
	 	'WARN': 3, //warn
	 	'ERROR': 2, //error
	 	'FATAL': 1, //This is NEVER invoked, possibly pointless
	 	'NONE': 0 //no logs
	 };

	 /**
	  * @type {number}
	  * @description The level of logger, default it is using FATAL
	  * @private
	  */
	 var logLevel = LEVELS.FATAL;
	/**
	 * @type {string}
	 * @description logName of the logger, for the purpose of returning a readable name if requested
	 * @private
	 */
	 var logName = "FATAL";

	/**
	 * @type {object}
	 * @description A holding container for the original console logging functions
	 * @private
	 */
	/* jshint ignore:start */
	var logOut = { /* Should we overload the prototype, or not?  The problem is the stack trace is unreliable but we're used to that */
		 log:console.log,
		 debug:console.debug,
		 info:console.info,
		 warn:console.warn,
		 error:console.error
	 };
	/* jshint ignore:end */

	/**
	 * @type function
	 * @description Empty function, for replacing unwanted logging functions
	 * @private
	 */
	 var doNothing = function(){ /* Intentional 'nothing' function */ };

	 //Start by wiping out the log prototypes
	 console.log = doNothing;
	 console.debug = doNothing;
	 console.info = doNothing;
	 console.warn = doNothing;
	 console.error = doNothing;

	 //We're going to main prototypes based on log level.  That means logger can actually be replaced back with console.log and we just enable/disable APIs.

	 /**
	  * @function init
	  * @description Initialize the logger
	  * @param config {InitConfig}
	  */
	 function init(config){
		 //console.log('initialising to level '+config.logLevel);
	 	if (config && config.hasOwnProperty('logLevel')) {
			setLevel(config.logLevel);
		} else {
			 setLevel('FATAL'); //apply default if nothing there
		}

	 }

	/**
	 * Prefixes log message with a group and timestamp
	 * @param level {string}
	 * @param msgargs {array} args from logger call
	 * @returns {array}
	 * @private
	 */
	 function IXFLog(level, msgargs) {
		 var args = Array.prototype.slice.call(msgargs);
		 return [level, '['+(new Date()).toISOString()+']'].concat(args);
	 }

	/**
	 * @function fatal
	 * @description If logLevel permits, call original console.error with a message
	 * @param {string} origin source of the call to Logger.fatal - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function fatal() {
		if (logLevel < LEVELS.FATAL) {
			return;
		}
		return logOut.error.apply(null, IXFLog('FATAL',arguments));
	}

	/**
	 * @function error
	 * @description If logLevel permits, call original console.error with a message
	 * @param {string} origin source of the call to Logger.error - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function error() {
		if (logLevel < LEVELS.ERROR) {
			return;
		}
		return logOut.error.apply(null, IXFLog('ERROR',arguments));
	}

	/**
	 * @function warn
	 * @description If logLevel permits, call original console.warn with a message
	 * @param {string} origin source of the call to Logger.warn - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function warn() {
		if (logLevel < LEVELS.WARN) {
			return;
		}
		return logOut.warn.apply(null, IXFLog('WARN', arguments));
	}

	/**
	 * @function info
	 * @description If logLevel permits, call original console.info with a message
	 * @param {string} origin source of the call to Logger.info - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function info() {
		if (logLevel < LEVELS.INFO) {
			return;
		}
		return logOut.info.apply(null, IXFLog('INFO',arguments));
	}

	/**
	 * @function debug
	 * @description If logLevel permits, call original console.debug with a message
	 * @param {string} origin source of the call to Logger.debug - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function debug() {
		if (logLevel < LEVELS.DEBUG) {
			return;
		}
		return logOut.debug.apply(null, IXFLog('DEBUG',arguments));
	}

	/**
	 * @function log
	 * @description If logLevel permits, call original console.log with a message
	 * @param {string} origin source of the call to Logger.log - typically a function or file reference
	 * @param {...any} * one or more strings, objects or any other structure
	 */
	function log() {
		if (logLevel < LEVELS.DEBUG) {
			return;
		}
		return logOut.log.apply(null, IXFLog('LOG',arguments));
	}

	/**
	 * @function setLevel
	 * @description Set a filtering level for determining which messages get permitted to log out
	 * @param level {string} Highest level to accept (ALL, INFO, WARN, FATAL, ERROR etc)
	 * @example
	 * Logger.setLevel('ERROR'); //Only show error / fatal messages
	 * Logger.setLevel('ALL'); //Log all errors
	 * Logger.setLevel('WARN'); //Log Warnings, Errors and fatals
	 * Logger.setLevel('NONE'); //No logging
	 */
	function setLevel(level) {
		if (LEVELS.hasOwnProperty(level)){
			logLevel = LEVELS[level];
			logName = level;
		} else {
			logLevel = LEVELS.FATAL;
			logName = 'FATAL';
		}
		console.log = doNothing;
		console.debug = doNothing;
		console.info = doNothing;
		console.warn = doNothing;
		console.error = doNothing;
		switch(logName) { //Note, no fallthrough!
			case 'ALL':
			case 'DEBUG':
				console.log = logOut.log;
				console.debug = logOut.debug; /* falls through */
			case 'INFO':
				console.info = logOut.info; /* falls through */
			case 'WARN':
				console.warn = logOut.warn; /* falls through */
			case 'ERROR':
			case 'FATAL':
				console.error = logOut.error; /* falls through */
		}

	}

	/**
	 * @function getLevel
	 * @description Get the current logging level
	 * @example
	 * var lvl = Logger.getLevel();
	 * console.log(lvl); // output such as 'DEBUG'
	 */
	function getLevel(){
		return logName;
	}


	/**
	 * @function reset
	 * @description Reset the logger
	 */
	function reset(){
		//logLevel = LEVELS.FATAL;
		setLevel('FATAL');
	}


	/**
	 * @function logger
	 * @description The function returns a singleton object for expose logging functionalities
	 * @private
	 * @return {object} Interface for logger
	 */
	function logger(){

		return Object.freeze({
			// Package level
			init: init,			
			setLevel : setLevel,
			getLevel : getLevel,

         	debug : debug,
	        error : error,
      		fatal : fatal,
			info : info,
			warn : warn,
			log : log,

			//reinstated for UTs
			reset:reset //This re-sets the init - probably makes sense to just call init from UT, actually
		});
	}

	export default logger();

//});